import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import styles from './Sidebar.module.scss';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { useNav, useUser } from '@/hooks';
import { ReactComponent as IconAbout } from '@/assets/icons/sidebar/about.svg';
import { ReactComponent as IconBlockSites } from '@/assets/icons/sidebar/block-sites.svg';
import { ReactComponent as IconCustomizedBlockPage } from '@/assets/icons/sidebar/customize-block-page.svg';
import { ReactComponent as IconFocusMode } from '@/assets/icons/sidebar/focus-mode.svg';
import { ReactComponent as IconInsights } from '@/assets/icons/sidebar/insights.svg';
import { ReactComponent as IconPasswordProtection } from '@/assets/icons/sidebar/password-protection.svg';
import { ReactComponent as IconSettings } from '@/assets/icons/sidebar/settings.svg';
import { ReactComponent as IconUsageLimit } from '@/assets/icons/sidebar/usage-limit.svg';
import { ReactComponent as IconPremiumLock } from '@/assets/icons/premium/premium-lock.svg';
import { ReactComponent as IconNewFeature } from '@/assets/icons/status/new.svg';
import { SidebarFooter } from './SidebarFooter/SidebarFooter';
import { LEGACY_ROUTES, ROUTES } from '@/constants/app/routes';
import { useDispatch, useSelector } from 'react-redux';
import { toggleSidebar } from '@/store/reducers';
import { selectFlagsConfig } from '@/store/selectors';
import { BlockingStatusToggle } from './BlockingStatusToggle/BlockingStatusToggle';
import { environment } from '@/environment';
import { TranslationKey } from '@/types/i18n.types';

enum ESidebarItemType {
  BlockSites = 'blockSites',
  UsageLimit = 'usageLimit',
  Insights = 'insights',
  FocusMode = 'focusMode',
  PasswordProtection = 'passwordProtection',
  CustomBlockPage = 'customBlockPage',
  Settings = 'settings',
  About = 'about',
}

interface ISidebarItem {
  key: ESidebarItemType;
  title: TranslationKey;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  isSelected: boolean;
  path: string;
  isPremium: boolean;
  isNew: boolean;
  isLegacy: boolean;
}
const sidebarItemsInitial: ISidebarItem[] = [
  {
    key: ESidebarItemType.BlockSites,
    title: 'menuItems.blockSites',
    isSelected: false,
    icon: IconBlockSites,
    path: ROUTES.HOME,
    isPremium: false,
    isNew: false,
    isLegacy: false,
  },
  {
    key: ESidebarItemType.UsageLimit,
    title: 'menuItems.usageLimit',
    isSelected: false,
    icon: IconUsageLimit,
    path: LEGACY_ROUTES.TIME_USAGE,
    isPremium: false,
    isNew: true,
    isLegacy: true,
  },
  {
    key: ESidebarItemType.Insights,
    title: 'menuItems.insights',
    isSelected: false,
    icon: IconInsights,
    path: LEGACY_ROUTES.INSIGHTS,
    isPremium: false,
    isNew: true,
    isLegacy: true,
  },
  {
    key: ESidebarItemType.FocusMode,
    title: 'menuItems.focusMode',
    isSelected: false,
    icon: IconFocusMode,
    path: LEGACY_ROUTES.FOCUS_MODE,
    isPremium: false,
    isNew: false,
    isLegacy: true,
  },
  {
    key: ESidebarItemType.PasswordProtection,
    title: 'menuItems.passwordProtection',
    isSelected: false,
    icon: IconPasswordProtection,
    path: LEGACY_ROUTES.PASSWORD_PROTECTION,
    isPremium: true,
    isNew: false,
    isLegacy: true,
  },
  {
    key: ESidebarItemType.CustomBlockPage,
    title: 'menuItems.customBlockPage',
    isSelected: false,
    icon: IconCustomizedBlockPage,
    path: ROUTES.CUSTOMIZE_BLOCK_PAGE,
    isPremium: true,
    isNew: false,
    isLegacy: false,
  },
  {
    key: ESidebarItemType.Settings,
    title: 'menuItems.settings',
    isSelected: false,
    icon: IconSettings,
    path: ROUTES.SETTINGS,
    isPremium: false,
    isNew: false,
    isLegacy: false,
  },
  {
    key: ESidebarItemType.About,
    title: 'menuItems.about',
    isSelected: false,
    icon: IconAbout,
    path: ROUTES.ABOUT,
    isPremium: false,
    isNew: false,
    isLegacy: false,
  },
];

export const Sidebar: React.FC = () => {
  const dispatch = useDispatch();
  const { isBlockingEnable: isBlockingEnabled } =
    useSelector(selectFlagsConfig);
  const { t } = useTranslation();
  const navHook = useNav();
  const { user } = useUser();
  const { pathname } = useLocation();

  const [sidebarItems, setSidebarItems] =
    useState<ISidebarItem[]>(sidebarItemsInitial);

  const onItemClick = (item: ISidebarItem) => {
    dispatch(toggleSidebar(false));
    if (item.isLegacy) {
      navHook.navLegacy(item.path);
      return;
    }
    navHook.nav(item.path);
  };

  const showPremiumIcon = (item: ISidebarItem) => {
    return item.isPremium && !user.isPremium;
  };

  const showNewIcon = (item: ISidebarItem) => {
    return item.isNew;
  };

  useEffect(() => {
    const tmpSidebarItems = sidebarItems.map((tmpItem: ISidebarItem) => {
      return { ...tmpItem, isSelected: false };
    });
    const path = pathname.replace(environment.baseUrl, '');
    const selectedItem = tmpSidebarItems.find(item => item.path === path);
    if (selectedItem) {
      selectedItem.isSelected = true;
    }
    setSidebarItems(tmpSidebarItems);
  }, [pathname]);

  return (
    <div className={styles.sidebar}>
      <div className={styles.sidebarTop}>
        <div className={styles.sidebarItems}>
          {sidebarItems.map((item: ISidebarItem) => {
            const Icon = item.icon;
            return (
              <div
                key={item.key}
                className={clsx(
                  styles.sidebarItem,
                  item.isSelected && styles.itemSelected
                )}
                onClick={() => {
                  onItemClick(item);
                }}
              >
                <div className={styles.sidebarItemLeft}>
                  <Icon
                    className={clsx(
                      styles.sidebarItemIcon,
                      item.isSelected && styles.sidebarItemIconSelected
                    )}
                  />
                  <div className={styles.sidebarItemTitle}>{t(item.title)}</div>
                </div>
                <div>
                  {showPremiumIcon(item) && (
                    <IconPremiumLock className={styles.premiumCta} />
                  )}
                  {showNewIcon(item) && (
                    <IconNewFeature className={styles.newFeature} />
                  )}
                </div>
              </div>
            );
          })}
        </div>
        {!isBlockingEnabled && (
          <>
            <div className={styles.sidebarDivider} />
            <div>
              <BlockingStatusToggle />
            </div>
          </>
        )}
      </div>
      <div className={styles.sidebarFooterWrapper}>
        <SidebarFooter />
      </div>
    </div>
  );
};
